import { useEffect, useState } from "react";
import { getRecommendedMaterials } from "../../services/materialsService";
import MaterialTeacherCard from "./MaterialTeacherCard";

// Owns the recommended-materials fetch + the loading / empty / grid triad,
// shared by the teacher dashboard's "Recommended Materials" section
// (PerformanceOverviewTab) and the library's Recommended tab (MaterialsLibrary).
//
// The two surfaces differ only in chrome, so the callers supply it:
//   gridClassName     — the grid layout (dashboard: md:grid-cols-2;
//                       library: sm:grid-cols-2 lg:grid-cols-3).
//   loadingFallback   — what to show while fetching (dashboard: <Spinner/>;
//                       library: a skeleton-card grid). Defaults to null.
//   emptyFallback     — what to show when the fetch returns nothing. Required
//                       in practice; defaults to null.
//   onError           — optional; called (instead of swallowing) when the
//                       fetch fails for a non-canceled reason, so the library
//                       can toast. The dashboard silently shows the empty state.
//
// AbortController + ERR_CANCELED/CanceledError handling and the
// "guard against !user?.uuid leaving the spinner stuck" fix live here so both
// callers inherit them.
export default function RecommendedMaterials({
  user,
  gridClassName,
  loadingFallback = null,
  emptyFallback = null,
  onError,
}) {
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Without a user there is nothing to recommend — clear state and bail so
    // the section never gets stuck on the loading fallback.
    if (!user?.uuid) {
      setRecommended([]);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    getRecommendedMaterials({ signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setRecommended(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError")
          return;
        setRecommended([]);
        onError?.(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // onError is intentionally omitted: callers pass a stable handler and we
    // don't want an inline arrow to re-trigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uuid]);

  if (loading) return loadingFallback;
  if (recommended.length === 0) return emptyFallback;

  return (
    <div className={gridClassName}>
      {recommended.map((entry) => (
        <MaterialTeacherCard
          key={entry.material.uuid}
          material={entry.material}
          matchedQuestions={entry.matched_questions || []}
        />
      ))}
    </div>
  );
}
